import { useState } from "react";
import { WhatsappLogo } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import Marquee from "@/components/Marquee";
import { Reveal } from "@/components/motion";
import { RESIDENTIAL_SERVICES, COMMERCIAL_SERVICES, PROCESSING_SERVICES, whatsappLink } from "@/lib/data";

const ServiceCard = ({ s, index }) => (
  <Reveal delay={(index % 3) * 0.06}>
    <div data-testid={`service-card-${index}`} className="group flex h-full flex-col border border-black/10 bg-white">
      <div className="aspect-[16/11] overflow-hidden">
        <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <h3 className="font-display text-xl font-medium tracking-tight text-ink">{s.title}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
        <a
          href={whatsappLink(`Hi Nagar Glass, I'd like to enquire about ${s.title}.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`service-enquire-${index}`}
          className="mt-6 inline-flex w-fit items-center gap-2 rounded-sm border border-ink px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-500 hover:bg-ink hover:text-offwhite"
        >
          <WhatsappLogo weight="fill" size={15} /> Enquire on WhatsApp
        </a>
      </div>
    </div>
  </Reveal>
);

const TABS = [
  {
    id: "residential",
    label: "Residential",
    subtitle: "Windows, doors, showers, mirrors, partitions, railings and custom glass work for modern homes.",
    data: RESIDENTIAL_SERVICES,
  },
  {
    id: "commercial",
    label: "Commercial",
    subtitle: "Storefronts, partitions, cabins, glazing and aluminium systems for offices, stores and showrooms.",
    data: COMMERCIAL_SERVICES,
  },
  {
    id: "processing",
    label: "Glass Processing",
    subtitle: "Toughened, laminated and EVA-fabric glass — processed and supplied for residential and commercial work.",
    data: PROCESSING_SERVICES,
  },
];

const Services = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHeader
        overline="Services · Glass & Aluminium"
        title={
          <>
            Everything glass. <br />
            <span className="italic">Everything aluminium.</span>
          </>
        }
        description="A complete range of residential and commercial solutions — designed, fabricated and installed by Nagar Glass. Explore each service and enquire directly on WhatsApp."
        testid="services-header"
      />

      {/* sticky sub-nav */}
      <div className="sticky top-[76px] z-30 border-y border-black/10 bg-offwhite/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] gap-8 px-5 md:px-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`services-tab-${t.id}`}
              onClick={() => scrollTo(t.id)}
              className="py-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-ink"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {TABS.map((t) => (
        <section key={t.id} id={t.id} data-testid={`services-${t.id}`} className="scroll-mt-32 border-b border-black/10 bg-offwhite py-20 md:py-28">
          <div className="mx-auto max-w-[1400px] px-5 md:px-8">
            <div className="grid grid-cols-1 items-end gap-6 md:grid-cols-12">
              <div className="md:col-span-8">
                <span className="eyebrow rule">{t.label}</span>
                <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-ink md:text-5xl">
                  {t.label} <span className="italic">Services</span>
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
                  {t.subtitle}
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {String(t.data.length).padStart(2, "0")} solutions
                </span>
              </div>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
              {t.data.map((s, i) => (
                <ServiceCard key={s.title + i} s={s} index={i} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <Marquee />
      <FinalCTA />
    </>
  );
};

export default Services;
